import { supabase } from "../../supabase/client";
import { mergeCommand, missingFieldsForCommand, parseAiOpsText } from "./aiOpsParser";
import { syncClientToFirstProfile } from "../../utils/clientProfileSync";

const APPROVER_ROLES = ["first", "operator"];
const RESERVATION_TYPES = ["일반", "학단", "통근", "투어"];
const LOCAL_FIRST_INTENTS = [
  "safety_create_draft",
  "reservation_search",
  "expense_create_draft",
  "vehicle_update_draft",
  "vehicle_create_draft",
  "driver_update_draft",
  "driver_create_draft",
  "user_create_draft",
  "bug_report_create_draft",
];
export const EXPENSE_KEYS = [
  "johab",
  "unyoung",
  "gps",
  "halbu",
  "geubyeo",
  "bohum",
  "sooksic",
  "jungbi",
  "jooyu",
  "secha",
  "4bohum",
  "tonghaeng",
  "joocha",
  "chooljang",
  "gwatae",
  "gita",
];

export const EXPENSE_LABELS = {
  johab: "조합비",
  unyoung: "운영비",
  gps: "GPS",
  halbu: "할부금",
  geubyeo: "급여",
  bohum: "보험료",
  sooksic: "숙식비",
  jungbi: "정비비",
  jooyu: "주유비",
  secha: "세차료",
  "4bohum": "4대보험",
  tonghaeng: "통행료",
  joocha: "주차료",
  chooljang: "출장비",
  gwatae: "과태료",
  gita: "기타비",
};

export const VEHICLE_UPDATE_LABELS = {
  car_number: "차량번호",
  car_name: "차명",
  driver: "기사명",
  phone: "핸드폰",
  year: "년식",
  driver_email: "기사 이메일",
  seat: "승차인원",
  career: "경력사항",
};

export const DRIVER_UPDATE_LABELS = {
  driver_name: "기사명",
  email: "기사 이메일",
  phone: "핸드폰",
};

export const DRIVER_CREATE_LABELS = {
  driver_name: "기사명",
  email: "기사 이메일",
  phone: "기사 전화번호",
  career: "경력사항",
};

export const USER_CREATE_LABELS = {
  name: "이름",
  user_id: "사용자 아이디",
  password: "비밀번호",
  system: "사용 시스템",
  branch: "지점명",
  team: "팀명",
  position: "직위명",
  email: "이메일",
  phone: "전화번호",
  address: "주소",
};

export const BUG_REPORT_LABELS = {
  title: "제목",
  content: "내용",
};

const VEHICLE_UPDATE_KEYS = Object.keys(VEHICLE_UPDATE_LABELS);
const DRIVER_UPDATE_KEYS = Object.keys(DRIVER_UPDATE_LABELS);
const DRIVER_CREATE_KEYS = Object.keys(DRIVER_CREATE_LABELS);
const USER_CREATE_KEYS = Object.keys(USER_CREATE_LABELS);
const BUG_REPORT_KEYS = Object.keys(BUG_REPORT_LABELS);

function getActor() {
  return {
    company: localStorage.getItem("email") || "",
    actorEmail: localStorage.getItem("userEmail") || localStorage.getItem("email") || "",
    role: localStorage.getItem("role") || "light",
  };
}

function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function addDays(dateStr, days) {
  const date = new Date(`${dateStr}T00:00:00`);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeReservationType(type) {
  return RESERVATION_TYPES.includes(type) ? type : "일반";
}

function normalizeCarNumber(value = "") {
  return String(value || "").replace(/\s+/g, "").slice(-4);
}

function cleanNullableText(value) {
  if (value === undefined) return undefined;
  const text = String(value ?? "").trim();
  return text === "" ? null : text;
}

const SPOKEN_EMAIL_WORDS = [
  ["이그잼플", "example"],
  ["이그잠플", "example"],
  ["익잼플", "example"],
  ["익잠플", "example"],
  ["드라이버", "driver"],
  ["어드민", "admin"],
  ["테스트", "test"],
  ["유저", "user"],
  ["사용자", "user"],
  ["구글메일", "gmail"],
  ["지메일", "gmail"],
  ["쥐메일", "gmail"],
  ["구글", "google"],
  ["네이버", "naver"],
  ["다음", "daum"],
  ["한메일", "hanmail"],
  ["카카오", "kakao"],
  ["야후", "yahoo"],
  ["아웃룩", "outlook"],
  ["핫메일", "hotmail"],
  ["컴", "com"],
  ["넷", "net"],
  ["오알지", "org"],
  ["코", "co"],
  ["케이알", "kr"],
  ["언더바", "_"],
  ["언더스코어", "_"],
  ["하이픈", "-"],
  ["대시", "-"],
  ["플러스", "+"],
  ["더블유", "w"],
  ["에이치", "h"],
  ["에프", "f"],
  ["에스", "s"],
  ["엑스", "x"],
  ["와이", "y"],
  ["제트", "z"],
  ["에이", "a"],
  ["비", "b"],
  ["씨", "c"],
  ["시", "c"],
  ["디", "d"],
  ["이", "e"],
  ["지", "g"],
  ["아이", "i"],
  ["제이", "j"],
  ["케이", "k"],
  ["엘", "l"],
  ["엠", "m"],
  ["엔", "n"],
  ["오", "o"],
  ["피", "p"],
  ["큐", "q"],
  ["알", "r"],
  ["티", "t"],
  ["유", "u"],
  ["브이", "v"],
];

function normalizeSpokenEmail(value = "") {
  let email = String(value || "").toLowerCase().trim();
  email = email
    .replace(/\s*(?:골뱅이|앳|엣)\s*/g, "@")
    .replace(/\s*(?:닷|점)\s*(?:컴|com)\b/g, ".com")
    .replace(/\s*(?:닷|점)\s*/g, ".")
    .replace(/\s*@\s*/g, "@")
    .replace(/\s*\.\s*/g, ".");

  [...SPOKEN_EMAIL_WORDS].sort((a, b) => b[0].length - a[0].length).forEach(([ko, en]) => {
    email = email.replace(new RegExp(ko, "g"), en);
  });

  return email
    .replace(/\s+/g, "")
    .replace(/\.(?:com|net|org|co|kr)(?:으로|로|이고|이며|입니다|이에요|예요|야|이야)?$/g, (match) =>
      match.replace(/(?:으로|로|이고|이며|입니다|이에요|예요|야|이야)$/g, "")
    )
    .replace(/[，,;；]+$/g, "")
    .replace(/[^a-z0-9._%+\-@]/g, "")
    .trim();
}

function cleanNullableEmail(value) {
  if (value === undefined) return undefined;
  const email = normalizeSpokenEmail(value);
  return email || null;
}

function cleanVehicleUpdates(updates = {}) {
  const cleaned = {};
  VEHICLE_UPDATE_KEYS.forEach((key) => {
    if (!(key in updates)) return;
    if (key === "year") {
      const value = String(updates[key] ?? "").replace(/[^0-9]/g, "");
      cleaned[key] = value ? Number(value) : null;
      return;
    }
    if (key === "car_number") {
      const value = String(updates[key] ?? "").replace(/\s+/g, "");
      cleaned[key] = value || null;
      cleaned.car_number_only = value ? value.slice(-4) : null;
      return;
    }
    if (key === "driver_email") {
      cleaned[key] = cleanNullableEmail(updates[key]);
      return;
    }
    cleaned[key] = cleanNullableText(updates[key]);
  });
  return cleaned;
}

function cleanDriverUpdates(updates = {}) {
  const cleaned = {};
  DRIVER_UPDATE_KEYS.forEach((key) => {
    if (!(key in updates)) return;
    if (key === "email") {
      cleaned[key] = cleanNullableEmail(updates[key]);
      return;
    }
    cleaned[key] = cleanNullableText(updates[key]);
  });
  return cleaned;
}

export function getVehicleChangeItems(before = {}, updates = {}) {
  return Object.entries(updates)
    .filter(([key]) => key !== "car_number_only" && VEHICLE_UPDATE_LABELS[key])
    .map(([key, value]) => ({
      key,
      label: VEHICLE_UPDATE_LABELS[key],
      before: before?.[key] ?? "",
      after: value ?? "",
    }));
}

export function getDriverChangeItems(before = {}, updates = {}) {
  return Object.entries(updates)
    .filter(([key]) => DRIVER_UPDATE_LABELS[key])
    .map(([key, value]) => ({
      key,
      label: DRIVER_UPDATE_LABELS[key],
      before: before?.[key] ?? "",
      after: value ?? "",
    }));
}

export function getDriverCreateItems(driver = {}) {
  return DRIVER_CREATE_KEYS
    .map((key) => ({
      key,
      label: DRIVER_CREATE_LABELS[key],
      value: driver?.[key] ?? "",
    }))
    .filter((item) => item.value !== "" && item.value !== null && item.value !== undefined);
}

export function getUserCreateItems(user = {}) {
  return USER_CREATE_KEYS
    .map((key) => ({
      key,
      label: USER_CREATE_LABELS[key],
      value: user?.[key] ?? "",
    }))
    .filter((item) => item.value !== "" && item.value !== null && item.value !== undefined);
}

export function getBugReportItems(report = {}) {
  return BUG_REPORT_KEYS
    .map((key) => ({
      key,
      label: BUG_REPORT_LABELS[key],
      value: report?.[key] ?? "",
    }))
    .filter((item) => item.value !== "" && item.value !== null && item.value !== undefined);
}

function hasExpenseAmount(command = {}) {
  return EXPENSE_KEYS.some((key) => toNumber(command[key], 0) > 0);
}

function summarizeExpenseItems(expense = {}) {
  return EXPENSE_KEYS
    .filter((key) => toNumber(expense[key], 0) > 0)
    .map((key) => `${EXPENSE_LABELS[key]} ${toNumber(expense[key], 0).toLocaleString()}원`)
    .join(", ");
}

export function getExpenseItems(expense = {}) {
  return EXPENSE_KEYS
    .map((key) => ({
      key,
      label: EXPENSE_LABELS[key],
      amount: toNumber(expense[key], 0),
    }))
    .filter((item) => item.amount > 0);
}

function localToday() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function uniqueBy(items = [], keyFn) {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(item);
  });
  return result;
}

function stripEmptyValues(value) {
  if (Array.isArray(value)) {
    return value.map(stripEmptyValues).filter((item) => {
      if (item === undefined || item === null || item === "") return false;
      if (Array.isArray(item)) return item.length > 0;
      if (typeof item === "object") return Object.keys(item).length > 0;
      return true;
    });
  }
  if (!value || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, item]) => [key, stripEmptyValues(item)])
      .filter(([, item]) => {
        if (item === undefined || item === null || item === "") return false;
        if (Array.isArray(item)) return item.length > 0;
        if (typeof item === "object") return Object.keys(item).length > 0;
        return true;
      })
  );
}

async function safeSupabaseList(buildQuery, fallback = []) {
  try {
    const { data, error } = await buildQuery();
    if (error) {
      console.warn("AI context fetch skipped:", error.message);
      return fallback;
    }
    return data || fallback;
  } catch (e) {
    console.warn("AI context fetch failed:", e?.message || e);
    return fallback;
  }
}

async function buildAiOpsAppContext(message, localResult = {}) {
  const { company, actorEmail, role } = getActor();
  const command = localResult.command || {};

  const [cars, drivers, reservations] = await Promise.all([
    safeSupabaseList(() =>
      supabase
        .from("cars")
        .select("id, car_number, car_number_only, car_name, driver, driver_email, phone, seat, direct")
        .eq("company", company)
        .order("car_number", { ascending: true })
        .limit(120)
    ),
    safeSupabaseList(() =>
      supabase
        .from("profiles")
        .select("id, driver_name, email, phone, car_number, career, role")
        .eq("company", company)
        .eq("role", "driver")
        .limit(120)
    ),
    safeSupabaseList(() => {
      let query = supabase
        .from("reservations")
        .select("id, date, time, arrival_time, user, user_phone, start, transit, destination, car_number, human_name, phone, reservation_type, seat, one_round")
        .eq("company", company)
        .order("date", { ascending: false })
        .order("time", { ascending: true })
        .limit(120);

      if (command.date) query = query.eq("date", command.date);
      return query;
    }),
  ]);

  const carCandidates = cars.map((car) =>
    stripEmptyValues({
      car_number: car.car_number,
      car_number_only: car.car_number_only || normalizeCarNumber(car.car_number),
      car_name: car.car_name,
      driver: car.driver,
      driver_email: car.driver_email,
      phone: car.phone,
      seat: car.seat,
      direct: car.direct,
    })
  );
  const driverCandidates = drivers.map((driver) =>
    stripEmptyValues({
      driver_name: driver.driver_name,
      email: driver.email,
      phone: driver.phone,
      car_number: driver.car_number,
      career: driver.career,
    })
  );
  const recentReservations = reservations.map((row) =>
    stripEmptyValues({
      date: row.date,
      time: row.time,
      arrival_time: row.arrival_time,
      reservation_type: row.reservation_type,
      user: row.user,
      user_phone: row.user_phone,
      start: row.start,
      transit: row.transit,
      destination: row.destination,
      car_number: row.car_number,
      human_name: row.human_name,
      phone: row.phone,
      seat: row.seat,
      one_round: row.one_round,
    })
  );

  const userRouteCandidates = uniqueBy(
    recentReservations
      .filter((row) => row.user || row.start || row.destination)
      .map((row) => ({
        user: row.user,
        user_phone: row.user_phone,
        start: row.start,
        transit: row.transit,
        destination: row.destination,
        reservation_type: row.reservation_type,
      })),
    (row) => [row.user, row.start, row.transit, row.destination, row.reservation_type].join("|")
  ).slice(0, 80);

  return stripEmptyValues({
    company,
    actor_email: actorEmail,
    actor_role: role,
    today: localToday(),
    user_message: message,
    supported_intents: [
      "create_reservation_draft",
      "driver_change_draft",
      "safety_create_draft",
      "reservation_search",
      "expense_create_draft",
      "vehicle_update_draft",
      "vehicle_create_draft",
      "driver_update_draft",
      "driver_create_draft",
      "user_create_draft",
      "bug_report_create_draft",
      "unknown",
    ],
    reservation_types: ["일반", "학단", "통근", "투어"],
    required_fields: {
      create_reservation_draft: ["reservation_type", "date", "time", "start", "destination", "user"],
      reservation_search: ["date"],
      expense_create_draft: ["date", "car_number", "one_or_more_expense_amounts"],
      vehicle_update_draft: ["target_car_number", "updates"],
      vehicle_create_draft: ["car_number"],
      driver_update_draft: ["target_driver_name_or_target_email", "updates"],
      driver_create_draft: ["driver_name", "phone"],
      user_create_draft: ["name", "user_id", "password", "branch", "team", "position", "email", "phone"],
      safety_create_draft: ["issue", "driver_name", "date", "summary"],
      bug_report_create_draft: ["title", "content"],
    },
    database_tables: {
      reservations: ["date", "time", "arrival_time", "reservation_type", "user", "user_phone", "start", "transit", "destination", "car_number", "human_name", "phone", "money", "driver_money", "seat", "one_round"],
      reservation_summary: ["start_date", "end_date", "time", "arrival_time", "reservation_type", "user", "user_phone", "start", "transit", "destination", "money", "driver_money"],
      cars: ["car_number", "car_number_only", "car_name", "driver", "driver_email", "phone", "seat", "career", "direct"],
      profiles: ["driver_name", "email", "phone", "car_number", "career", "role"],
      "cars-expenses": EXPENSE_KEYS.concat(["date", "car_number", "human_name", "bigo"]),
    },
    candidates: {
      cars: carCandidates,
      drivers: driverCandidates,
      recent_reservations: recentReservations,
      user_routes: userRouteCandidates,
    },
    local_parser_result: localResult,
  });
}

function buildLocalDateTime(date, time = "00:00") {
  return `${date}T${time || "00:00"}:00+09:00`;
}

function buildDateRows(command) {
  const nights = Math.max(0, toNumber(command.nights, 0));
  const buses = Math.max(1, toNumber(command.buses, command.reservation_type === "통근" ? 1 : 1));
  const dates = [];

  for (let dayOffset = 0; dayOffset <= nights; dayOffset += 1) {
    const date = addDays(command.date, dayOffset);
    const count = command.reservation_type === "통근" ? 1 : buses;
    for (let i = 0; i < count; i += 1) dates.push(date);
  }

  return dates;
}

function buildReservationRows(command, company, groupId) {
  const reservationType = normalizeReservationType(command.reservation_type);
  const seat = command.seat || "대형";
  const carNumber = command.car_number || "";
  const rows = buildDateRows({ ...command, reservation_type: reservationType });

  return rows.map((date) => ({
    date,
    time: command.time || null,
    start: command.start || "",
    transit: command.transit || "",
    destination: command.destination || "",
    user: command.user || "",
    user_phone: command.user_phone || "",
    money: toNumber(command.money, 0),
    memo: command.note || "",
    one_round: command.round || "왕복",
    number_of_car: 1,
    car_number: carNumber,
    human_name: command.human_name || "",
    phone: command.phone || "",
    reservation_type: reservationType,
    seat,
    seat_detail: command.seat_detail || null,
    days: toNumber(command.nights, 0),
    company,
    reservation_group_id: groupId,
  }));
}

function buildReservationSummary(command, company, groupId) {
  const nights = Math.max(0, toNumber(command.nights, 0));
  const reservationType = normalizeReservationType(command.reservation_type);
  const startDate = command.date;
  const endDate = addDays(startDate, nights);

  return {
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
    days: nights,
    time: command.time || null,
    number_of_car: reservationType === "통근" ? 1 : Math.max(1, toNumber(command.buses, 1)),
    user: command.user || "",
    user_phone: command.user_phone || "",
    reservation_type: reservationType,
    car_type: command.seat || "대형",
    start: command.start || "",
    destination: command.destination || "",
    transit: command.transit || "",
    money: toNumber(command.money, 0),
    company,
    seat_detail: command.seat_detail || null,
  };
}

function normalizeReservationApprovalDates(approval) {
  if (!approval || approval.action_type !== "reservation_create") return approval;

  const payload = approval.payload || {};
  const summary = payload.summary || {};
  const reservations = payload.reservations || [];
  const startDate = summary.start_date;
  if (!startDate || reservations.length === 0) return approval;

  const nights = Math.max(0, toNumber(summary.days, 0));
  const perDay = Math.max(1, toNumber(summary.number_of_car, 1));
  const normalizedSummary = {
    ...summary,
    end_date: addDays(startDate, nights),
  };
  const normalizedReservations = reservations.map((row, index) => ({
    ...row,
    date: addDays(startDate, Math.floor(index / perDay)),
  }));
  const normalizedPayload = {
    ...payload,
    summary: normalizedSummary,
    reservations: normalizedReservations,
  };

  return {
    ...approval,
    payload: normalizedPayload,
    after_data: {
      ...(approval.after_data || {}),
      summary: normalizedSummary,
      reservations: normalizedReservations,
    },
  };
}

async function normalizeApprovalBeforeApply(approvalId) {
  const { company } = getActor();
  const { data, error } = await supabase
    .from("ai_ops_approvals")
    .select("*")
    .eq("id", approvalId)
    .eq("company", company)
    .single();

  if (error) throw error;
  const normalized = normalizeReservationApprovalDates(data);

  if (JSON.stringify(normalized.payload) === JSON.stringify(data.payload)) {
    return data;
  }

  const { data: updated, error: updateError } = await supabase
    .from("ai_ops_approvals")
    .update({
      payload: normalized.payload,
      after_data: normalized.after_data,
      validation: {
        ...(data.validation || {}),
        date_normalized_before_apply: true,
      },
    })
    .eq("id", approvalId)
    .eq("company", company)
    .eq("status", "pending")
    .select("*")
    .single();

  if (updateError) throw updateError;
  return updated;
}

async function insertAuditLog(payload) {
  const { error } = await supabase.from("ai_ops_audit_logs").insert([payload]);
  if (error) console.warn("AI 감사 로그 저장 실패:", error.message);
}

async function saveAiRequest({ userMessage, aiResult, status = "draft" }) {
  const { company, actorEmail, role } = getActor();
  const { data, error } = await supabase
    .from("ai_ops_requests")
    .insert([
      {
        company,
        actor_email: actorEmail,
        actor_role: role,
        user_message: userMessage,
        assistant_message: aiResult.reply || "",
        intent: aiResult.intent || "unknown",
        structured_command: aiResult.command || {},
        missing_fields: aiResult.missing_fields || [],
        status,
      },
    ])
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

async function createApproval({ requestId, actionType, title, payload, beforeData, afterData, validation }) {
  const { company, actorEmail, role } = getActor();
  const { data, error } = await supabase
    .from("ai_ops_approvals")
    .insert([
      {
        company,
        request_id: requestId || null,
        action_type: actionType,
        title,
        status: "pending",
        requested_by: actorEmail,
        requested_role: role,
        payload: payload || {},
        before_data: beforeData || {},
        after_data: afterData || {},
        validation: validation || {},
      },
    ])
    .select("*")
    .single();

  if (error) throw error;

  await insertAuditLog({
    company,
    approval_id: data.id,
    request_id: requestId || null,
    actor_email: actorEmail,
    actor_role: role,
    event_type: "approval_created",
    before_data: beforeData || {},
    after_data: afterData || {},
    metadata: { action_type: actionType, title },
  });

  return data;
}

export async function interpretAiOpsMessage(message, context = {}) {
  const { company, actorEmail, role } = getActor();
  const previousCommand = context.previousCommand || {};
  const previousIntent = context.previousIntent || "";
  const localResult = parseAiOpsText(message, {
    previousCommand,
    previousIntent,
  });
  const shouldPreferLocal = LOCAL_FIRST_INTENTS.includes(localResult.intent);
  const appContext = await buildAiOpsAppContext(message, localResult);

  try {
    const { data, error } = await supabase.functions.invoke("ai-ops-assistant", {
      body: {
        message,
        company,
        actor_email: actorEmail,
        actor_role: role,
        today: appContext.today || localToday(),
        previous_intent: previousIntent,
        previous_command: previousCommand,
        local_result: localResult,
        app_context: appContext,
      },
    });

    if (!error && data?.intent) {
      const intent = (data.intent === "unknown" || shouldPreferLocal) && localResult.intent !== "unknown"
        ? localResult.intent
        : data.intent;
      const command =
        shouldPreferLocal
          ? mergeCommand(mergeCommand(previousCommand, data.command || {}), localResult.command || {})
          : mergeCommand(mergeCommand(previousCommand, localResult.command || {}), data.command || {});
      const missing = missingFieldsForCommand(intent, command);
      return {
        ...data,
        intent,
        command,
        missing_fields: missing,
        reply: missing.length
          ? `필수 정보가 더 필요합니다: ${missing.join(", ")}`
          : data.reply || localResult.reply,
        source: "edge-function",
      };
    }
  } catch (e) {
    console.warn("AI Edge Function 호출 실패, 로컬 파서 사용:", e?.message || e);
  }

  return localResult;
}

export async function makeReservationDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = {
    ...aiResult.command,
    reservation_type: normalizeReservationType(aiResult.command?.reservation_type),
  };
  const missing = missingFieldsForCommand("create_reservation_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const groupId = command.group_id || newId();
  const reservations = buildReservationRows(command, company, groupId);
  const summary = buildReservationSummary(command, company, groupId);

  return {
    ok: true,
    type: "reservation_create",
    title: `${summary.start_date} ${summary.time || ""} ${summary.reservation_type} 예약 초안`,
    userMessage,
    command,
    payload: {
      group_id: groupId,
      summary,
      reservations,
    },
    beforeData: {},
    afterData: { summary, reservations },
    validation: {
      row_count: reservations.length,
      requires_approval: true,
      rule: "예약 신규 등록은 승인 전 DB에 반영하지 않습니다.",
    },
  };
}

async function loadRowsForDriverChange(company, date, driverName) {
  const { data, error } = await supabase
    .from("reservations")
    .select("*")
    .eq("company", company)
    .eq("date", date)
    .order("time", { ascending: true });

  if (error) throw error;

  const normalized = String(driverName || "").replace(/\s+/g, "");
  return (data || []).filter((row) => {
    const human = String(row.human_name || "").replace(/\s+/g, "");
    return human.includes(normalized);
  });
}

async function loadCandidateCars(company, date, affectedRows, fromDriverName) {
  const [{ data: cars, error: carsError }, { data: offRows, error: offError }, { data: dayRows, error: dayError }] =
    await Promise.all([
      supabase
        .from("cars")
        .select("id, car_number, car_number_only, driver, phone, driver_email, direct, seat, seat_detail")
        .eq("company", company)
        .eq("direct", true),
      supabase
        .from("holidays_driver")
        .select("driver")
        .eq("company", company)
        .lte("from", date)
        .gte("to", date),
      supabase
        .from("reservations")
        .select("id, car_number, human_name, time")
        .eq("company", company)
        .eq("date", date),
    ]);

  if (carsError) throw carsError;
  if (offError) throw offError;
  if (dayError) throw dayError;

  const offDrivers = new Set((offRows || []).map((row) => row.driver || ""));
  const affectedIds = new Set((affectedRows || []).map((row) => row.id));
  const usedCars = new Set(
    (dayRows || [])
      .filter((row) => !affectedIds.has(row.id))
      .map((row) => normalizeCarNumber(row.car_number))
      .filter(Boolean)
  );
  const usedDrivers = new Set(
    (dayRows || [])
      .filter((row) => !affectedIds.has(row.id))
      .map((row) => row.human_name || "")
      .filter(Boolean)
  );
  const targetSeat = affectedRows?.[0]?.seat || "";

  return (cars || [])
    .filter((car) => car.driver && car.driver !== fromDriverName)
    .filter((car) => !offDrivers.has(car.driver))
    .filter((car) => !usedDrivers.has(car.driver))
    .filter((car) => !usedCars.has(normalizeCarNumber(car.car_number_only || car.car_number)))
    .map((car) => ({
      ...car,
      car_number_for_reservation: car.car_number_only || normalizeCarNumber(car.car_number),
      score: targetSeat && car.seat === targetSeat ? 2 : 1,
    }))
    .sort((a, b) => b.score - a.score || String(a.driver).localeCompare(String(b.driver)));
}

export async function makeDriverChangeDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("driver_change_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const affectedRows = await loadRowsForDriverChange(company, command.date, command.from_driver_name);
  if (affectedRows.length === 0) {
    return {
      ok: false,
      reply: `${command.date}에 ${command.from_driver_name} 기사로 배정된 예약을 찾지 못했습니다.`,
      missing_fields: [],
    };
  }

  const candidates = await loadCandidateCars(company, command.date, affectedRows, command.from_driver_name);
  if (candidates.length === 0) {
    return {
      ok: false,
      reply: "휴무/당일 배차 충돌을 제외하니 대체 가능한 직접관리 차량/기사를 찾지 못했습니다.",
      missing_fields: [],
    };
  }

  const selected = candidates[0];
  const reservationUpdates = affectedRows.map((row) => ({
    id: row.id,
    date: row.date,
    before: {
      car_number: row.car_number || "",
      human_name: row.human_name || "",
      phone: row.phone || "",
      driver_email: row.driver_email || "",
    },
    after: {
      car_number: selected.car_number_for_reservation || "",
      human_name: selected.driver || "",
      phone: selected.phone || "",
      driver_email: selected.driver_email || "",
    },
    context: {
      user: row.user || "",
      time: row.time || "",
      start: row.start || "",
      destination: row.destination || "",
      reservation_type: row.reservation_type || "",
    },
  }));

  const leave = {
    company,
    driver: command.from_driver_name,
    car_number: affectedRows[0]?.car_number || null,
    name: command.reason || "병가",
    from: command.date,
    to: command.date,
  };

  const payload = {
    leave,
    reservation_updates: reservationUpdates.map((item) => ({
      id: item.id,
      ...item.after,
    })),
  };

  return {
    ok: true,
    type: "driver_sick_reassign",
    title: `${command.date} ${command.from_driver_name} 기사 ${leave.name} 처리 및 ${selected.driver} 기사 재배정`,
    userMessage,
    command,
    payload,
    beforeData: { affected_reservations: affectedRows },
    afterData: {
      leave,
      reservation_updates: reservationUpdates,
      selected_candidate: selected,
      alternatives: candidates.slice(1, 6),
    },
    validation: {
      affected_count: affectedRows.length,
      candidate_count: candidates.length,
      conflict_rule: "같은 날짜의 기존 배차 기사/차량과 기사 휴무를 제외했습니다.",
      requires_approval: true,
      salary_impact: "병가/휴무 기록이 생성되므로 급여 검토가 필요할 수 있습니다.",
    },
  };
}

export function makeSafetyDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("safety_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const safety = {
    company,
    issue: command.issue,
    driver_name: command.driver_name,
    car_number: command.car_number || null,
    date: buildLocalDateTime(command.date, command.time || "00:00"),
    place: command.place || null,
    opponent: command.opponent || null,
    summary: command.summary || null,
    insurance: command.insurance || null,
    process: command.process || null,
    etc: command.etc || null,
    surcharge: command.surcharge || null,
  };

  return {
    ok: true,
    type: "safety_create",
    title: `${command.date} ${command.driver_name} 기사 ${command.issue} 안전관리 등록`,
    userMessage,
    command,
    payload: { safety },
    beforeData: {},
    afterData: { safety },
    validation: {
      requires_approval: true,
      rule: "AI 안전관리 등록은 승인 전 DB에 반영하지 않습니다.",
    },
  };
}

async function findCarForExpense(company, carNumber) {
  const normalized = normalizeCarNumber(carNumber);
  if (!normalized) return null;

  const { data, error } = await supabase
    .from("cars")
    .select("id, car_number, car_number_only, driver, phone, driver_email")
    .eq("company", company)
    .or(`car_number.eq.${carNumber},car_number_only.eq.${normalized},car_number.ilike.%${normalized}%`)
    .limit(5);

  if (error) throw error;
  return data?.[0] || null;
}

export async function makeExpenseDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("expense_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  if (!hasExpenseAmount(command)) {
    return { ok: false, missing_fields: ["지출 항목/금액"], reply: "지출 항목과 금액이 필요합니다." };
  }

  const car = await findCarForExpense(company, command.car_number);
  if (!car) {
    return {
      ok: false,
      reply: `차량번호 ${command.car_number}를 차량정보에서 찾지 못했습니다. 차량번호를 확인해 주세요.`,
      missing_fields: [],
    };
  }

  const expense = {
    company,
    date: command.date,
    car_number: car.car_number_only || normalizeCarNumber(car.car_number || command.car_number),
    human_name: car.driver || command.human_name || "",
    bigo: command.bigo || userMessage || "",
  };

  EXPENSE_KEYS.forEach((key) => {
    expense[key] = toNumber(command[key], 0);
  });

  const driverMismatch =
    command.human_name && car.driver && !String(car.driver).replace(/\s+/g, "").includes(String(command.human_name).replace(/\s+/g, ""));

  return {
    ok: true,
    type: "expense_create",
    title: `${expense.date} ${expense.car_number} ${expense.human_name} 지출 등록 초안`,
    userMessage,
    command,
    payload: { expense },
    beforeData: { matched_car: car },
    afterData: { expense },
    validation: {
      requires_approval: true,
      source_table: "cars-expenses",
      matched_car_number: car.car_number || "",
      matched_car_number_only: car.car_number_only || "",
      requested_driver: command.human_name || "",
      matched_driver: car.driver || "",
      driver_mismatch: driverMismatch,
      items: summarizeExpenseItems(expense),
      rule: "AI 지출 등록은 승인 후 cars-expenses 테이블에 반영됩니다.",
    },
  };
}

async function findVehicleForUpdate(company, carNumber) {
  const normalized = normalizeCarNumber(carNumber);
  if (!normalized) return null;

  const { data, error } = await supabase
    .from("cars")
    .select("*")
    .eq("company", company)
    .or(`car_number.eq.${carNumber},car_number_only.eq.${normalized},car_number.ilike.%${normalized}%`)
    .limit(5);

  if (error) throw error;
  return data?.[0] || null;
}

async function findDriverForUpdate(company, command = {}) {
  let query = supabase
    .from("profiles")
    .select("id, role, email, driver_name, phone, car_number, company")
    .eq("company", company)
    .eq("role", "driver");

  const targetEmail = cleanNullableEmail(command.target_email);
  if (targetEmail) {
    query = query.eq("email", targetEmail);
  } else if (command.target_driver_name) {
    query = query.eq("driver_name", String(command.target_driver_name).trim());
  } else {
    return null;
  }

  const { data, error } = await query.limit(5);
  if (error) throw error;
  if (!data?.length) return null;

  if (command.target_driver_name && command.target_email) {
    const exact = data.find(
      (row) =>
        String(row.driver_name || "").trim() === String(command.target_driver_name || "").trim() &&
        String(row.email || "").trim() === targetEmail
    );
    return exact || data[0];
  }

  return data[0];
}

async function findDriverForCreate(company, email, phone) {
  let query = supabase
    .from("profiles")
    .select("id, email, driver_name, phone, company")
    .eq("company", company)
    .eq("role", "driver");

  if (email && phone) {
    query = query.or(`email.eq.${email},phone.eq.${phone}`);
  } else if (email) {
    query = query.eq("email", email);
  } else if (phone) {
    query = query.eq("phone", phone);
  } else {
    return [];
  }

  const { data, error } = await query.limit(5);
  if (error) throw error;
  return data || [];
}

async function findClientForCreate(company, userId, email) {
  let query = supabase
    .from("client")
    .select("id, name, user_id, email")
    .eq("company", company);

  if (userId && email) {
    query = query.or(`user_id.eq.${userId},email.eq.${email}`);
  } else if (userId) {
    query = query.eq("user_id", userId);
  } else if (email) {
    query = query.eq("email", email);
  } else {
    return [];
  }

  const { data, error } = await query.limit(5);
  if (error) throw error;
  return data || [];
}

function cleanVehicleCreatePayload(command = {}, company = "") {
  const carNumber = String(command.car_number || "").replace(/\s+/g, "");
  return {
    company,
    car_number: carNumber,
    car_name: cleanNullableText(command.car_name),
    driver: cleanNullableText(command.driver),
    driver_email: cleanNullableEmail(command.driver_email),
    phone: cleanNullableText(command.phone),
    year: command.year ? Number(String(command.year).replace(/[^0-9]/g, "")) : null,
    seat: cleanNullableText(command.seat),
    seat_detail: cleanNullableText(command.seat_detail),
    career: cleanNullableText(command.career),
    car_number_only: carNumber ? carNumber.slice(-4) : null,
  };
}

function cleanDriverCreatePayload(command = {}, company = "") {
  return {
    company,
    driver_name: cleanNullableText(command.driver_name),
    email: cleanNullableEmail(command.email),
    phone: cleanNullableText(command.phone),
    career: cleanNullableText(command.career),
    car_number: cleanNullableText(command.car_number),
  };
}

function cleanUserCreatePayload(command = {}, company = "") {
  return {
    company,
    name: cleanNullableText(command.name),
    user_id: cleanNullableText(command.user_id),
    password: cleanNullableText(command.password),
    system: cleanNullableText(command.system) || "기사",
    branch: cleanNullableText(command.branch),
    team: cleanNullableText(command.team),
    position: cleanNullableText(command.position),
    email: cleanNullableEmail(command.email),
    phone: cleanNullableText(command.phone),
    address: cleanNullableText(command.address),
  };
}

function cleanBugReportPayload(command = {}, company = "") {
  return {
    company,
    title: cleanNullableText(command.title),
    content: cleanNullableText(command.content),
  };
}

export async function makeVehicleCreateDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("vehicle_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const vehicle = cleanVehicleCreatePayload(command, company);
  const normalized = normalizeCarNumber(vehicle.car_number);
  const [{ data: existing, error }, { data: sameLast4, error: sameLast4Error }] = await Promise.all([
    supabase
      .from("cars")
      .select("id, car_number, car_number_only, driver")
      .eq("company", company)
      .eq("car_number", vehicle.car_number)
      .limit(1),
    supabase
      .from("cars")
      .select("id, car_number, car_number_only, driver")
      .eq("company", company)
      .eq("car_number_only", normalized)
      .limit(5),
  ]);

  if (error) throw error;
  if (sameLast4Error) throw sameLast4Error;
  if (existing?.length) {
    return {
      ok: false,
      reply: `차량번호 ${vehicle.car_number}와 같은 차량이 이미 차량정보에 있습니다.`,
      missing_fields: [],
    };
  }

  const sameLast4Cars = (sameLast4 || []).filter((row) => row.car_number !== vehicle.car_number);
  const sameLast4Note =
    sameLast4Cars.length > 0
      ? `끝 4자리 ${normalized}가 같은 차량이 ${sameLast4Cars.length}건 있습니다.`
      : "";
  return {
    ok: true,
    type: "vehicle_create",
    title: `${vehicle.car_number} 차량 추가 등록 초안`,
    userMessage,
    command,
    payload: { vehicle },
    beforeData: { same_last4_cars: sameLast4Cars },
    afterData: { vehicle },
    validation: {
      requires_approval: true,
      source_table: "cars",
      profile_sync: "기사 이메일이 있으면 승인 후 profiles.car_number를 차량 끝 4자리로 동기화합니다.",
      duplicate_rule: "차량번호 전체가 같은 경우만 중복으로 차단합니다.",
      same_last4_warning: sameLast4Note,
      rule: "AI 차량 추가 등록은 승인 후 cars 테이블에 반영됩니다.",
    },
  };
}

export async function makeVehicleUpdateDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("vehicle_update_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const car = await findVehicleForUpdate(company, command.target_car_number);
  if (!car) {
    return {
      ok: false,
      reply: `차량번호 ${command.target_car_number}를 차량정보에서 찾지 못했습니다.`,
      missing_fields: [],
    };
  }

  const updates = cleanVehicleUpdates(command.updates || {});
  if (Object.keys(updates).filter((key) => key !== "car_number_only").length === 0) {
    return { ok: false, missing_fields: ["수정할 항목"], reply: "수정할 차량 정보 항목이 필요합니다." };
  }

  const before = {};
  VEHICLE_UPDATE_KEYS.forEach((key) => {
    before[key] = car[key] ?? "";
  });
  before.car_number_only = car.car_number_only || "";

  const after = { ...before, ...updates };
  const changes = getVehicleChangeItems(before, updates);

  return {
    ok: true,
    type: "vehicle_update",
    title: `${car.car_number_only || normalizeCarNumber(car.car_number)} 차량 정보 수정 초안`,
    userMessage,
    command,
    payload: {
      car_id: car.id,
      target_car_number: command.target_car_number,
      updates,
      expected_before: command.expected_before || {},
    },
    beforeData: { car: before },
    afterData: { car: after, changes },
    validation: {
      requires_approval: true,
      source_table: "cars",
      profile_sync: "기사 이메일 변경 시 profiles.car_number를 함께 동기화합니다.",
      changed_fields: changes.map((item) => item.label),
      rule: "AI 차량 정보 수정은 승인 후 cars 테이블에 반영됩니다.",
    },
  };
}

export async function makeDriverUpdateDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("driver_update_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const driver = await findDriverForUpdate(company, command);
  if (!driver) {
    const target = command.target_email || command.target_driver_name || "";
    return {
      ok: false,
      reply: `${target} 기사 정보를 찾지 못했습니다.`,
      missing_fields: [],
    };
  }

  const updates = cleanDriverUpdates(command.updates || {});
  if (Object.keys(updates).length === 0) {
    return { ok: false, missing_fields: ["수정할 항목"], reply: "수정할 기사 정보 항목이 필요합니다." };
  }

  const before = {
    id: driver.id,
    email: driver.email || "",
    driver_name: driver.driver_name || "",
    phone: driver.phone || "",
    car_number: driver.car_number || "",
    company: driver.company || "",
  };
  const after = { ...before, ...updates };
  const changes = getDriverChangeItems(before, updates);

  return {
    ok: true,
    type: "driver_update",
    title: `${before.driver_name || before.email} 기사 정보 수정 초안`,
    userMessage,
    command,
    payload: {
      profile_id: driver.id,
      target_driver_name: command.target_driver_name || "",
      target_email: cleanNullableEmail(command.target_email) || "",
      updates,
      expected_before: command.expected_before || {},
    },
    beforeData: { driver: before },
    afterData: { driver: after, changes },
    validation: {
      requires_approval: true,
      source_table: "profiles",
      email_cascade: "기사 이메일 변경 시 cars, maintenance, receipts, salary의 driver_email을 함께 갱신합니다.",
      changed_fields: changes.map((item) => item.label),
      rule: "AI 기사 정보 수정은 승인 후 profiles 테이블에 반영됩니다.",
    },
  };
}

export async function makeDriverCreateDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("driver_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const driver = cleanDriverCreatePayload(command, company);
  const existing = await findDriverForCreate(company, driver.email, driver.phone);
  if (existing.length > 0) {
    return {
      ok: false,
      reply: `이미 등록된 기사 정보가 있습니다: ${existing.map((row) => row.email || row.phone || row.driver_name).join(", ")}`,
      missing_fields: [],
    };
  }

  return {
    ok: true,
    type: "driver_create",
    title: `${driver.driver_name} 기사 추가 등록 초안`,
    userMessage,
    command,
    payload: { driver },
    beforeData: {},
    afterData: { driver },
    validation: {
      requires_approval: true,
      source_table: "profiles",
      auth_user_create: "승인 후 create-driver Edge Function을 호출해 기사 Auth 계정과 profiles 정보를 생성합니다.",
      initial_password: "create-driver 함수의 기본 초기 비밀번호 정책을 따릅니다.",
      rule: "AI 기사 추가 등록은 승인 후 create-driver 함수로 반영됩니다.",
    },
  };
}

export async function makeUserCreateDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("user_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const user = cleanUserCreatePayload(command, company);
  const existing = await findClientForCreate(company, user.user_id, user.email);
  if (existing.length > 0) {
    return {
      ok: false,
      reply: `이미 등록된 사용자가 있습니다: ${existing.map((row) => row.user_id || row.email || row.name).join(", ")}`,
      missing_fields: [],
    };
  }

  return {
    ok: true,
    type: "user_create",
    title: `${user.name} 사용자 추가 등록 초안`,
    userMessage,
    command,
    payload: { user },
    beforeData: {},
    afterData: { user },
    validation: {
      requires_approval: true,
      source_table: "client",
      duplicate_rule: "같은 회사의 사용자 아이디 또는 이메일이 이미 있으면 차단합니다.",
      rule: "AI 사용자 추가 등록은 승인 후 client 테이블에 반영됩니다.",
    },
  };
}

export async function makeBugReportDraft(aiResult, userMessage) {
  const { company } = getActor();
  const command = aiResult.command || {};
  const missing = missingFieldsForCommand("bug_report_create_draft", command);
  if (missing.length > 0) {
    return { ok: false, missing_fields: missing, reply: `필수 정보가 더 필요합니다: ${missing.join(", ")}` };
  }

  const report = cleanBugReportPayload(command, company);
  return {
    ok: true,
    type: "bug_report_create",
    title: `${report.title} 버그 신고 초안`,
    userMessage,
    command,
    payload: { report },
    beforeData: {},
    afterData: { report },
    validation: {
      requires_approval: true,
      source_table: "bug-report",
      rule: "AI 버그 신고는 승인 후 bug-report 테이블에 반영됩니다.",
    },
  };
}

export async function createApprovalFromDraft(draft, aiResult) {
  const request = await saveAiRequest({
    userMessage: draft.userMessage || "",
    aiResult: {
      ...aiResult,
      command: draft.command || aiResult.command || {},
      reply: draft.title,
    },
    status: "pending_approval",
  });

  return createApproval({
    requestId: request.id,
    actionType: draft.type,
    title: draft.title,
    payload: draft.payload,
    beforeData: draft.beforeData,
    afterData: draft.afterData,
    validation: draft.validation,
  });
}

export async function fetchAiApprovals(status = "pending") {
  const { company } = getActor();
  let query = supabase
    .from("ai_ops_approvals")
    .select("*")
    .eq("company", company)
    .order("created_at", { ascending: false });

  if (status && status !== "all") query = query.eq("status", status);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function readFunctionError(error) {
  const fallback = error?.message || "Edge Function 호출에 실패했습니다.";
  const context = error?.context;
  if (!context) return fallback;

  try {
    if (typeof context.json === "function") {
      const body = await context.json();
      if (body?.error) return `${body.error}${body.stage ? ` (stage: ${body.stage})` : ""}`;
      return JSON.stringify(body);
    }
  } catch (e) {
    // Fall through to text/fallback.
  }

  try {
    if (typeof context.text === "function") {
      const text = await context.text();
      return text || fallback;
    }
  } catch (e) {
    // Fall through to fallback.
  }

  return fallback;
}

async function applyDriverCreateApproval(approval) {
  const { company, actorEmail, role } = getActor();
  const driver = approval.payload?.driver || {};
  const { data, error } = await supabase.functions.invoke("create-driver", {
    body: {
      email: cleanNullableEmail(driver.email) || null,
      driver_name: driver.driver_name || "",
      car_number: driver.car_number || null,
      phone: driver.phone || null,
      career: driver.career || null,
      company,
    },
  });

  if (error) throw new Error(await readFunctionError(error));
  if (data?.error) throw new Error(data.error);

  const result = {
    ok: true,
    action_type: "driver_create",
    profile_id: data?.user_id || null,
    initialPassword: data?.initialPassword || null,
    warning: data?.warning || null,
  };

  const { error: updateError } = await supabase
    .from("ai_ops_approvals")
    .update({
      status: "applied",
      approved_by: actorEmail,
      approved_at: new Date().toISOString(),
      result,
    })
    .eq("id", approval.id)
    .eq("company", company)
    .eq("status", "pending");

  if (updateError) throw updateError;

  if (approval.request_id) {
    const { error: requestError } = await supabase
      .from("ai_ops_requests")
      .update({ status: "applied" })
      .eq("id", approval.request_id)
      .eq("company", company);
    if (requestError) throw requestError;
  }

  await insertAuditLog({
    company,
    request_id: approval.request_id || null,
    approval_id: approval.id,
    actor_email: actorEmail,
    actor_role: role,
    event_type: "approval_applied",
    before_data: approval.before_data || {},
    after_data: approval.after_data || {},
    metadata: result,
  });

  return result;
}

async function applyUserCreateApproval(approval) {
  const { company, actorEmail, role } = getActor();
  const user = cleanUserCreatePayload(approval.payload?.user || {}, company);
  const payload = { ...user, company };

  const { data, error } = await supabase
    .from("client")
    .insert([payload])
    .select("*")
    .single();

  if (error) throw error;
  await syncClientToFirstProfile(data || payload, {
    company,
    companyName: data?.company_name || payload.company_name,
  });

  const result = {
    ok: true,
    action_type: "user_create",
    client_id: data?.id || null,
  };

  const { error: updateError } = await supabase
    .from("ai_ops_approvals")
    .update({
      status: "applied",
      approved_by: actorEmail,
      approved_at: new Date().toISOString(),
      result,
    })
    .eq("id", approval.id)
    .eq("company", company)
    .eq("status", "pending");

  if (updateError) throw updateError;

  if (approval.request_id) {
    const { error: requestError } = await supabase
      .from("ai_ops_requests")
      .update({ status: "applied" })
      .eq("id", approval.request_id)
      .eq("company", company);
    if (requestError) throw requestError;
  }

  await insertAuditLog({
    company,
    request_id: approval.request_id || null,
    approval_id: approval.id,
    actor_email: actorEmail,
    actor_role: role,
    event_type: "approval_applied",
    before_data: approval.before_data || {},
    after_data: approval.after_data || {},
    metadata: result,
  });

  return result;
}

async function applyBugReportApproval(approval) {
  const { company, actorEmail, role } = getActor();
  const report = cleanBugReportPayload(approval.payload?.report || {}, company);

  const { data, error } = await supabase
    .from("bug-report")
    .insert([{ ...report, company }])
    .select("*")
    .single();

  if (error) throw error;

  const result = {
    ok: true,
    action_type: "bug_report_create",
    bug_report_id: data?.id || null,
  };

  const { error: updateError } = await supabase
    .from("ai_ops_approvals")
    .update({
      status: "applied",
      approved_by: actorEmail,
      approved_at: new Date().toISOString(),
      result,
    })
    .eq("id", approval.id)
    .eq("company", company)
    .eq("status", "pending");

  if (updateError) throw updateError;

  if (approval.request_id) {
    const { error: requestError } = await supabase
      .from("ai_ops_requests")
      .update({ status: "applied" })
      .eq("id", approval.request_id)
      .eq("company", company);
    if (requestError) throw requestError;
  }

  await insertAuditLog({
    company,
    request_id: approval.request_id || null,
    approval_id: approval.id,
    actor_email: actorEmail,
    actor_role: role,
    event_type: "approval_applied",
    before_data: approval.before_data || {},
    after_data: approval.after_data || {},
    metadata: result,
  });

  return result;
}

export async function approveAiApproval(approvalId) {
  const { company, actorEmail, role } = getActor();
  if (!APPROVER_ROLES.includes(role)) {
    throw new Error("승인 권한이 없습니다. first/operator 권한 사용자만 승인할 수 있습니다.");
  }

  await normalizeApprovalBeforeApply(approvalId);

  const { data: approval, error: approvalError } = await supabase
    .from("ai_ops_approvals")
    .select("*")
    .eq("id", approvalId)
    .eq("company", company)
    .single();

  if (approvalError) throw approvalError;

  if (approval?.action_type === "driver_create") {
    return applyDriverCreateApproval(approval);
  }

  if (approval?.action_type === "user_create") {
    return applyUserCreateApproval(approval);
  }

  if (approval?.action_type === "bug_report_create") {
    return applyBugReportApproval(approval);
  }

  if (
    approval?.action_type === "vehicle_update" ||
    approval?.action_type === "vehicle_create" ||
    approval?.action_type === "driver_update"
  ) {
    const functionName =
      approval.action_type === "vehicle_create"
        ? "apply_ai_ops_vehicle_create"
        : approval.action_type === "driver_update"
        ? "apply_ai_ops_driver_update"
        : "apply_ai_ops_vehicle_update";
    const { data, error } = await supabase.rpc(functionName, {
      p_approval_id: approvalId,
      p_actor_email: actorEmail,
    });

    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase.rpc("apply_ai_ops_approval", {
    p_approval_id: approvalId,
    p_actor_email: actorEmail,
  });

  if (error) throw error;
  return data;
}

export async function rejectAiApproval(approvalId, reason = "") {
  const { company, actorEmail, role } = getActor();
  if (!APPROVER_ROLES.includes(role)) {
    throw new Error("반려 권한이 없습니다. first/operator 권한 사용자만 반려할 수 있습니다.");
  }

  const { data, error } = await supabase
    .from("ai_ops_approvals")
    .update({
      status: "rejected",
      rejected_by: actorEmail,
      rejected_at: new Date().toISOString(),
      result: { reason },
    })
    .eq("id", approvalId)
    .eq("company", company)
    .eq("status", "pending")
    .select("*")
    .single();

  if (error) throw error;

  await insertAuditLog({
    company,
    approval_id: approvalId,
    actor_email: actorEmail,
    actor_role: role,
    event_type: "approval_rejected",
    metadata: { reason },
  });

  return data;
}

export function canApproveAiOps() {
  return APPROVER_ROLES.includes(getActor().role);
}
